// src/pages/super-admin/DrugPanels/DrugPanelList.tsx
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useListDrugPanelsQuery,
  useDeleteDrugPanelMutation,
  type DrugPanel,
} from "@/features/drug-panels/services/drugPanelApi";
import { toast } from "sonner";
import CreateDrugPanel from "./CreateDrugPanel";
import EditDrugPanel from "./EditDrugPanel";
import { Search, Plus, Edit2, Trash2, Microscope, Layers, FlaskConical } from "lucide-react";

export default function DrugPanelList() {
  const [search, setSearch] = useState("");
  const [componentSearch, setComponentSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState<DrugPanel | null>(null);

  const { data: panels = [], isLoading } = useListDrugPanelsQuery({
    search,
    component: componentSearch,
  });
  const [deletePanel] = useDeleteDrugPanelMutation();

  const handleDelete = async (id: number) => {
    if (confirm("Are you sure you want to delete this panel?")) {
      try {
        await deletePanel(id).unwrap();
        toast.success("Panel deleted successfully");
      } catch (err) {
        toast.error("Failed to delete panel");
      }
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
             <Microscope className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-slate-100">Drug Test Panels</h1>
            <p className="text-slate-500 text-sm font-medium">Configure and manage complex multi-substance screening panels.</p>
          </div>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-5 h-11 font-bold shadow-lg shadow-purple-100 dark:shadow-none transition-all active:scale-95">
              <Plus className="w-4 h-4 mr-2" />
              Add Panel
            </Button>
          </DialogTrigger>
          <DialogContent className="w-full max-w-2xl rounded-2xl border-2">
            <DialogHeader>
              <DialogTitle className="text-xl font-black text-slate-900">Create New Drug Panel</DialogTitle>
            </DialogHeader>
            <CreateDrugPanel onClose={() => setCreateOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Filters */}
      <Card className="border-none shadow-sm bg-slate-50/50 dark:bg-slate-900/50">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group relative">
              <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-2 ml-1">
                Panel Identity
              </label>
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-purple-500 transition-colors" />
                <Input
                  placeholder="Search by Panel Name or Alias..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 h-12 border-slate-200 bg-white rounded-xl focus-visible:ring-purple-500"
                />
              </div>
            </div>
            <div className="group relative">
              <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-2 ml-1">
                Substance Component
              </label>
              <div className="relative">
                <FlaskConical className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-purple-500 transition-colors" />
                <Input
                  placeholder="e.g., THC, Opiates, Cocaine..."
                  value={componentSearch}
                  onChange={(e) => setComponentSearch(e.target.value)}
                  className="pl-10 h-12 border-slate-200 bg-white rounded-xl focus-visible:ring-purple-500"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Panels List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isLoading ? (
            <div className="col-span-full py-20 text-center animate-pulse">
               <div className="inline-block p-4 bg-muted rounded-full mb-4">
                  <Microscope className="w-8 h-8 text-muted-foreground" />
               </div>
               <p className="text-muted-foreground font-bold">Sifting through panel data...</p>
            </div>
          ) : panels.length === 0 ? (
            <div className="col-span-full py-20 text-center bg-white rounded-2xl border border-dashed">
               <div className="inline-block p-4 bg-slate-50 rounded-full mb-4">
                  <Layers className="w-8 h-8 text-slate-300" />
               </div>
               <h3 className="text-lg font-bold text-slate-900">No panels match your query</h3>
               <p className="text-slate-500">Try adjusting your filters or add a new panel.</p>
            </div>
          ) : (
            panels.map((panel) => (
              <Card key={panel.id} className="group border-slate-100 hover:border-purple-200 hover:shadow-xl hover:shadow-purple-500/5 transition-all duration-300 rounded-2xl overflow-hidden overflow-hidden overflow-hidden overflow-hidden overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row h-full">
                    <div className="p-6 flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className="space-y-1">
                          <h3 className="text-xl font-black text-slate-900 dark:text-slate-100 group-hover:text-purple-600 transition-colors">
                            {panel.panelName}
                          </h3>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-purple-50 text-purple-700 font-bold border-purple-100">
                              {panel.numComponents}-Panel
                            </Badge>
                            <Badge
                              className={`font-bold border-0 ${
                                panel.status === "ACTIVE"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                            >
                              {panel.status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-2 italic">
                        "{panel.panelDescription}"
                      </p>
                      
                      <div className="space-y-3">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5 ml-0.5">Substances Included</p>
                          <div className="flex flex-wrap gap-1.5">
                            {panel.components.map((comp) => (
                              <Badge key={comp} variant="outline" className="rounded-md border-slate-200 text-slate-600 bg-white">
                                {comp}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        {panel.aliases.length > 0 && (
                          <div className="pt-2 border-t border-slate-50">
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Commercial Aliases</p>
                            <p className="text-xs text-slate-500 font-medium leading-tight">
                              {panel.aliases.join(" • ")}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-4 sm:border-l border-slate-50 bg-slate-50/30 flex sm:flex-col justify-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-xl transition-all"
                        onClick={() => {
                          setSelectedPanel(panel);
                          setEditOpen(true);
                        }}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-xl transition-all"
                        onClick={() => handleDelete(panel.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Drug Panel</DialogTitle>
          </DialogHeader>
          {selectedPanel && (
            <EditDrugPanel
              panel={selectedPanel}
              onClose={() => {
                setEditOpen(false);
                setSelectedPanel(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
